import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputSwitch } from 'primereact/inputswitch';
import { FilterMatchMode } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import Loader from '../../Shared/Loader/Loader';
import APIConstant from '../../../utils/pathConstants';
import api from '../../../utils/apiClient';
import CommonConfig from '../../../utils/constant';
import Toast from '../../Shared/Toast/Toast';
import DeleteConfirmation from '../../Dialogs/DeleteConfirmation';
import AddEditGeofenceConfiguration from './AddEditGeofenceConfiguration';
import { useCatchResponse } from '../../../utils/useCatchResponse';

const TYPE_LABEL = {
  CircularFixed200: 'Circular (200 m)',
  CircularCustom: 'Circular (custom)',
  RectangleCustom: 'Rectangle',
  PolygonCustom: 'Polygon',
};

const GeofenceConfiguration = () => {
  const { catchResponse } = useCatchResponse();
  const [list, setList] = useState([]);
  const [attendanceTypeMap, setAttendanceTypeMap] = useState({});
  const [expandedRows, setExpandedRows] = useState(null);
  const [masterData, setMasterData] = useState({});
  const [geofenceId, setGeofenceId] = useState('');
  const [visible, setVisible] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    GeofenceName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [securityUserID, setSecurityUserID] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [rows, setRows] = useState(10);
  const [first, setFirst] = useState(0);
  const [isDeleteConfirmation, setIsDeleteConfirmation] = useState(false);
  const [deleteId, setDeleteId] = useState('');

  useEffect(() => {
    const loginData = CommonConfig.loginData();
    setSecurityUserID(loginData?._id);
    setOrganizationId(loginData?.OrganizationId || '');
    GetList(loginData?.OrganizationId || '');
    fetchAttendanceTypes(loginData?._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAttendanceTypes = async (createdBy) => {
    try {
      const res = await api.post(APIConstant.path.getAttendanceTypeConfigurationList, { CreatedBy: createdBy });
      if (res.success) {
        const map = {};
        (res.data || []).forEach((t) => { map[String(t._id)] = t.AttendanceName; });
        setAttendanceTypeMap(map);
      }
    } catch (err) {
      catchResponse(err);
    }
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    const _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const GetList = async (orgId) => {
    try {
      Loader.show();
      const data = { OrganizationId: orgId };
      await api
        .post(APIConstant.path.getGeofenceConfigurationList, data)
        .then((response) => {
          if (response.success) {
            setList(response.data || []);
          }
          Loader.hide();
        })
        .catch((err) => {
          Loader.hide();
          catchResponse(err);
        });
    } catch (err) {
      catchResponse(err);
    }
  };

  const toggleStatus = async (row) => {
    try {
      Loader.show();
      const nextStatus = row.Status === 'Active' ? 'Inactive' : 'Active';
      await api
        .post(APIConstant.path.updateGeofenceConfigurationStatus, {
          id: row._id,
          Status: nextStatus,
          UpdatedBy: securityUserID,
        })
        .then((res) => {
          if (res.success) {
            Toast.success({ message: res.message });
            GetList(organizationId);
          } else {
            Toast.error({ message: res.message || res.error });
            Loader.hide();
          }
        })
        .catch((err) => {
          Loader.hide();
          catchResponse(err);
        });
    } catch (err) {
      catchResponse(err);
    }
  };

  const DeleteGeofence = async () => {
    try {
      Loader.show();
      await api
        .post(APIConstant.path.deleteGeofenceConfiguration, {
          id: deleteId,
          DeletedBy: securityUserID,
        })
        .then((res) => {
          if (res.success) {
            Toast.success({ message: 'Deleted successfully' });
          } else {
            Toast.error({ message: res.message || res.error });
          }
          setDeleteId('');
          handleClose();
          GetList(organizationId);
        })
        .catch((err) => {
          Loader.hide();
          setDeleteId('');
          handleClose();
          catchResponse(err);
        });
    } catch (err) {
      catchResponse(err);
    }
  };

  const onCancel = () => {
    setVisible(false);
    setGeofenceId('');
    setIsEdit(false);
    setMasterData({});
    GetList(organizationId);
  };

  const renderFooter = () => (
    <div className="dialog-footer p-d-flex p-ai-center p-jc-end">
      <Button label="Cancel" className="btn-text-action-secondary" onClick={handleClose} />
      <Button label="Delete" className="btn-text-action-primary" onClick={() => DeleteGeofence()} />
    </div>
  );

  const handleClose = () => setIsDeleteConfirmation(false);

  const typeBodyTemplate = (row) => TYPE_LABEL[row.GeofenceType] || row.GeofenceType || '-';

  const userTypesBodyTemplate = (row) => {
    const ut = row.UserType || (Array.isArray(row.UserTypes) ? row.UserTypes[0] : '');
    if (!ut) return '-';
    const staffCount = Array.isArray(row.StaffIds) ? row.StaffIds.length : 0;
    return (
      <div className="p-d-flex p-ai-center" style={{ gap: 6, flexWrap: 'wrap' }}>
        <Tag value={ut} />
        {ut === 'Staff' && (
          <small style={{ color: '#666' }}>
            {staffCount > 0 ? `${staffCount} selected` : 'All staff'}
          </small>
        )}
      </div>
    );
  };

  const summaryBodyTemplate = (row) => {
    if (row.GeofenceType === 'CircularFixed200' || row.GeofenceType === 'CircularCustom') {
      const c = row.Center || {};
      const r = row.GeofenceType === 'CircularFixed200' ? 200 : row.RadiusMeters;
      if (c.Lat == null || c.Lng == null) return '-';
      return `${Number(c.Lat).toFixed(6)}, ${Number(c.Lng).toFixed(6)} · r=${r}m`;
    }
    if (row.GeofenceType === 'RectangleCustom') {
      const b = row.RectangleBounds || {};
      if (!b.NE || !b.SW) return '-';
      return `NE ${Number(b.NE.Lat).toFixed(4)},${Number(b.NE.Lng).toFixed(4)} · SW ${Number(b.SW.Lat).toFixed(4)},${Number(b.SW.Lng).toFixed(4)}`;
    }
    if (row.GeofenceType === 'PolygonCustom') {
      const n = Array.isArray(row.PolygonPoints) ? row.PolygonPoints.length : 0;
      return `${n} points`;
    }
    return '-';
  };

  const attendanceTypeBodyTemplate = (row) => attendanceTypeMap[String(row.AttendanceTypeId)] || '-';

  const statusBodyTemplate = (row) => (
    <InputSwitch checked={row.Status === 'Active'} onChange={() => toggleStatus(row)} />
  );

  const actionBodyTemplate = (row) => (
    <div className="p-d-flex p-ai-center p-jc-end">
      <Button
        icon="pi pi-pencil"
        className="e-sction-btn p-m-0"
        onClick={() => {
          setGeofenceId(row._id);
          setMasterData(row);
          setIsEdit(true);
          setVisible(true);
        }}
      />
      <Button
        icon="pi pi-trash"
        className="e-sction-btn p-m-0 p-m-2"
        onClick={() => {
          setIsDeleteConfirmation(true);
          setDeleteId(row._id);
        }}
      />
    </div>
  );

  const srNoBodyTemplate = (_data, props) => props.rowIndex + 1;

  const parents = (list || []).filter((r) => !r.ParentGeofenceId);
  const subsByParent = (list || []).reduce((acc, r) => {
    if (r.ParentGeofenceId) {
      (acc[String(r.ParentGeofenceId)] = acc[String(r.ParentGeofenceId)] || []).push(r);
    }
    return acc;
  }, {});

  const rowExpansionTemplate = (row) => {
    const subs = subsByParent[String(row._id)] || [];
    if (subs.length === 0) {
      return <div style={{ padding: 12, color: '#888' }}>No sub-geofences.</div>;
    }
    return (
      <div style={{ padding: 8 }}>
        <DataTable value={subs} responsiveLayout="scroll">
          <Column field="GeofenceName" header="Sub-Geofence Name" />
          <Column body={typeBodyTemplate} header="Type" />
          <Column body={summaryBodyTemplate} header="Details" />
          <Column body={statusBodyTemplate} header="Status" />
          <Column body={actionBodyTemplate} header="Action" />
        </DataTable>
      </div>
    );
  };

  const expanderAllowed = (row) => !row.ParentGeofenceId;

  return (
    <div className="wayment-owner-dashboard et-master-page">
      <div className="heading-area p-d-flex p-ai-center p-jc-between">
        <div className="p-d-flex p-ai-baseline">
          <i className="pi pi-map-marker"></i>
          <h2>Geofence Configuration</h2>
        </div>
        <div className="p-d-flex p-ai-center p-jc-between">
          <div className="searchbar-area e-searchbar-area">
            <i className="pi pi-search" />
            <InputText
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Global Search"
            />
          </div>
          <Button icon="pi pi-plus" className="e-h-btn" onClick={() => setVisible(true)} />
        </div>
      </div>
      <DataTable
        value={parents}
        dataKey="_id"
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        paginator
        rows={rows}
        first={first}
        onPage={onPageChange}
        filters={filters}
        globalFilterFields={['GeofenceName']}
        responsiveLayout="scroll"
        className="custom-table"
        scrollHeight="calc(100vh - 120px)"
        size="small"
        paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink JumpToPageInput"
        rowsPerPageOptions={[5, 10, 20]}
        currentPageReportTemplate="Showing {first}-{last} of {totalRecords} records"
      >
        <Column expander={expanderAllowed} style={{ width: '3rem' }} />
        <Column field="id" body={srNoBodyTemplate} header="Sr No." />
        <Column field="GeofenceName" header="Name" />
        <Column body={typeBodyTemplate} header="Type" />
        <Column body={userTypesBodyTemplate} header="User Type" />
        <Column body={attendanceTypeBodyTemplate} header="Attendance Type" />
        <Column body={summaryBodyTemplate} header="Details" />
        <Column body={(row) => (subsByParent[String(row._id)] || []).length} header="Sub-Geofences" />
        <Column body={statusBodyTemplate} header="Status" />
        <Column body={actionBodyTemplate} header="Action" />
      </DataTable>
      <Dialog
        header={geofenceId ? 'Edit Geofence' : 'Add Geofence'}
        className="dialog-popup"
        visible={visible}
        onHide={() => onCancel()}
        draggable={false}
        position="center"
        style={{ width: '70vw' }}
      >
        <AddEditGeofenceConfiguration
          masterData={masterData}
          isEdit={isEdit}
          ID={geofenceId}
          parentOptions={parents}
          onCancel={onCancel}
        />
      </Dialog>
      <Dialog
        visible={isDeleteConfirmation}
        onHide={handleClose}
        header={
          <div className="p-d-flex p-jc-between p-ai-center">
            <span>Delete Confirmation</span>
          </div>
        }
        footer={renderFooter()}
        draggable={false}
        dismissableMask
        position="center"
        style={{ width: '450px' }}
      >
        <DeleteConfirmation />
      </Dialog>
    </div>
  );
};

export default GeofenceConfiguration;
